import { useQuery } from "@tanstack/react-query";

import { fetchAdsets, fetchCampaigns, getMetaAdsStatus } from "@/services/metaAds.service";

/** Where a launched ad should run; all null means a new campaign. */
export interface MetaTarget {
  campaignId: string | null;
  campaignName: string | null;
  adsetId: string | null;
  adsetName: string | null;
}

export const NO_TARGET: MetaTarget = { campaignId: null, campaignName: null, adsetId: null, adsetName: null };

export interface MetaTargetOption {
  id: string;
  name: string;
  status: string;
  objective?: string;
}

function rows(res: unknown): MetaTargetOption[] {
  const data = (res as { data?: unknown[] } | undefined)?.data;
  if (!Array.isArray(data)) return [];
  return data
    .map((r) => {
      const o = r as Record<string, unknown>;
      return {
        id: String(o.id ?? ""),
        name: typeof o.name === "string" ? o.name : String(o.id ?? ""),
        status: String(o.effective_status ?? o.status ?? ""),
        objective: typeof o.objective === "string" ? o.objective : undefined,
      };
    })
    .filter((r) => r.id);
}

/**
 * The brand's existing Meta campaigns, and the ad sets of one of them, for
 * "run this ad inside…" pickers. Nothing loads until Meta is connected with
 * a default ad account, so the pickers simply stay hidden otherwise.
 */
export function useMetaTargets(campaignId: string | null, enabled = true) {
  const status = useQuery({ queryKey: ["meta-ads-status"], queryFn: getMetaAdsStatus, enabled });
  const ready = enabled && !!status.data?.connected && !status.data.deauthorized && !!status.data.defaultAdAccountId;
  const campaigns = useQuery({
    queryKey: ["meta-targets", "campaigns"],
    queryFn: () => fetchCampaigns({ fields: "id,name,status,effective_status,objective", limit: "200" }),
    enabled: ready,
    select: rows,
    staleTime: 60_000,
  });
  const adsets = useQuery({
    queryKey: ["meta-targets", "adsets", campaignId],
    queryFn: () => fetchAdsets(campaignId!, { fields: "id,name,status,effective_status", limit: "200" }),
    enabled: ready && !!campaignId,
    select: rows,
    staleTime: 60_000,
  });
  return {
    ready,
    pixelId: status.data?.metaPixelId ?? null,
    campaigns: campaigns.data ?? [],
    campaignsLoading: ready && campaigns.isLoading,
    adsets: adsets.data ?? [],
    adsetsLoading: ready && !!campaignId && adsets.isLoading,
  };
}
